<script setup>
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    required: true,
  },
  isDropTarget: {
    type: Boolean,
    default: false,
  },
  draggedConversationId: {
    type: Number,
    default: null,
  },
  persistingConversationIds: {
    type: Array,
    default: () => [],
  },
  currentUserId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'assign-to-me',
  'drag-start',
  'drag-end',
  'drag-enter',
  'drag-leave',
  'drop',
]);

const onDrop = event => {
  event.preventDefault();
  emit('drop', props.stage.id);
};
</script>

<template>
  <section
    class="flex min-h-[28rem] flex-col rounded-3xl border border-n-weak bg-n-alpha-2 p-4"
    :class="{
      'ring-2 ring-n-brand shadow-lg': isDropTarget,
    }"
    @dragover.prevent
    @dragenter.prevent="$emit('drag-enter', stage.id)"
    @dragleave.prevent="$emit('drag-leave', stage.id)"
    @drop="onDrop"
  >
    <header class="mb-4 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span :class="['h-2.5 w-2.5 rounded-full', stage.accentClass]" />
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ $t(stage.titleKey) }}
          </h2>
        </div>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t(stage.descriptionKey) }}
        </p>
      </div>
      <span
        class="rounded-full bg-n-alpha-black2 px-2 py-1 text-xs font-semibold text-n-slate-12"
      >
        {{ conversations.length }}
      </span>
    </header>

    <div class="flex flex-1 flex-col gap-3">
      <KanbanCard
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :is-dragging="draggedConversationId === conversation.id"
        :is-persisting="persistingConversationIds.includes(conversation.id)"
        :can-assign-to-me="
          !!currentUserId &&
          !conversation.meta?.assignee &&
          stage.id !== 'done'
        "
        @assign-to-me="$emit('assign-to-me', $event)"
        @drag-start="$emit('drag-start', $event)"
        @drag-end="$emit('drag-end')"
      />

      <div
        v-if="!conversations.length"
        class="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-n-strong px-4 py-8 text-center text-sm text-n-slate-11"
      >
        {{ $t('KANBAN.EMPTY_COLUMN') }}
      </div>
    </div>
  </section>
</template>
